import { s } from '@/theme/tokens';
import { DialogFooterActions } from '@/ui/composites/DialogFooterActions';
import { dialogRhythm } from '@/ui/composites/dialogRhythm';
import { FormCard } from '@/ui/composites/FormCard';
import {
  Dialog,
  DialogContent,
  IconButton,
  type DialogContentProps,
  type DialogProps,
} from '@/ui/primitives';
import { Box } from '@/ui/primitives/Box';
import { CloseIcon } from '@/ui/primitives/icons';
import type { SpacingToken } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { KeyboardEventHandler, ReactElement, ReactNode } from 'react';

type DialogLayoutProps = {
  open: boolean;
  title?: string;
  titleIcon?: ReactNode;
  titleSlot?: ReactNode;
  headerSlot?: ReactNode;
  titleSx?: SxProps<Theme>;
  description?: ReactNode;
  actions?: ReactNode;
  footerStartSlot?: ReactNode;
  padding?: SpacingToken | null;
  headerGap?: SpacingToken;
  contentOffset?: SpacingToken;
  actionsAlign?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  actionsGap?: SpacingToken;
  actionsSx?: SxProps<Theme>;
  footerGap?: SpacingToken;
  contentGap?: SpacingToken;
  gap?: SpacingToken;
  dialogSx?: DialogProps['sx'];
  paperSx?: SxProps<Theme>;
  contentSx?: DialogContentProps['sx'];
  surfaceVariant?: 'default' | 'dialog' | 'settings';
  onContentKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
  onClose: DialogProps['onClose'];
  closeAction?: () => void;
  closeAriaLabel?: string;
  showCloseButton?: boolean;
  disableClose?: boolean;
  allowTopBarInteraction?: boolean;
  width?: 'compact' | 'standard' | 'wide';
  scroll?: DialogProps['scroll'];
  children: ReactNode;
};

const dialogMaxWidthByWidth: Record<
  NonNullable<DialogLayoutProps['width']>,
  DialogProps['maxWidth']
> = {
  compact: 'xs',
  standard: 'sm',
  wide: 'md',
};

export const DialogLayout = ({
  open,
  title,
  titleIcon,
  titleSlot,
  headerSlot,
  titleSx,
  description,
  actions,
  footerStartSlot,
  padding = 'xl',
  headerGap = dialogRhythm.headerGap,
  actionsAlign = 'flex-end',
  actionsGap = dialogRhythm.actionsGap,
  actionsSx,
  footerGap = dialogRhythm.footerGap,
  contentGap = dialogRhythm.contentGap,
  gap = dialogRhythm.bodyGap,
  dialogSx,
  paperSx,
  contentSx,
  surfaceVariant = 'dialog',
  contentOffset = dialogRhythm.contentOffset,
  onContentKeyDownCapture,
  onClose,
  closeAction,
  closeAriaLabel = 'Close dialog',
  showCloseButton = true,
  disableClose = false,
  allowTopBarInteraction = false,
  width = 'standard',
  scroll = 'paper',
  children,
}: DialogLayoutProps): ReactElement => {
  const hasContent = children !== null && children !== undefined && children !== false;
  const closeButton =
    showCloseButton && !disableClose && closeAction ? (
      <IconButton aria-label={closeAriaLabel} onClick={closeAction} size="large">
        <CloseIcon fontSize="medium" />
      </IconButton>
    ) : null;
  const resolvedTitleSlot =
    titleSlot || closeButton ? (
      <Box sx={{ alignItems: 'center', display: 'flex', gap: s('sm') }}>
        {titleSlot}
        {closeButton}
      </Box>
    ) : undefined;
  const resolvedPadding =
    padding ?? (surfaceVariant === 'settings' || surfaceVariant === 'dialog' ? 'xl' : 'lg');
  const baseContentSx: DialogContentProps['sx'] = () => ({
    overflowX: 'hidden',
    overflowY: 'auto',
  });
  const resolvedContentSx: DialogContentProps['sx'] = contentSx
    ? ([
        baseContentSx,
        ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
      ] as DialogContentProps['sx'])
    : baseContentSx;
  const contentSxArray = Array.isArray(resolvedContentSx)
    ? resolvedContentSx
    : ([resolvedContentSx] as Array<DialogContentProps['sx']>);
  const footer = actions ? (
    <DialogFooterActions
      startSlot={footerStartSlot}
      gap={actionsGap}
      justify={actionsAlign}
      sx={actionsSx}
    >
      {actions}
    </DialogFooterActions>
  ) : undefined;
  const basePaperSx: SxProps<Theme> = {
    p: s('none'),
    display: 'flex',
    flexDirection: 'column',
  };
  const resolvedPaperSx = paperSx
    ? ([basePaperSx, ...(Array.isArray(paperSx) ? paperSx : [paperSx])] as SxProps<Theme>)
    : basePaperSx;
  const paperSlotProps = {
    sx: resolvedPaperSx,
    'aria-label': title,
  };
  const dialogSlotProps: DialogProps['slotProps'] = disableClose
    ? {
        paper: paperSlotProps,
        backdrop: {
          onClick: (event) => {
            event.stopPropagation();
          },
        },
      }
    : { paper: paperSlotProps };
  const topBarOffsetSx: DialogProps['sx'] | undefined = allowTopBarInteraction
    ? {
        zIndex: 9,
        '& .MuiBackdrop-root': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
        },
        '& .MuiDialog-container': {
          top: 'var(--app-topbar-height, 54px)',
          height: 'calc(100% - var(--app-topbar-height, 54px))',
          maxHeight: 'calc(100% - var(--app-topbar-height, 54px))',
        },
      }
    : undefined;
  const hasDialogSx = Boolean(topBarOffsetSx) || Boolean(dialogSx);
  const resolvedDialogSx = hasDialogSx
    ? ([topBarOffsetSx, dialogSx].filter(Boolean) as DialogProps['sx'])
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : onClose}
      disableEscapeKeyDown={disableClose}
      fullWidth
      maxWidth={dialogMaxWidthByWidth[width]}
      scroll={scroll}
      slotProps={dialogSlotProps}
      sx={resolvedDialogSx}
    >
      <DialogContent
        sx={[{ p: s('none') }, ...contentSxArray]}
        onKeyDownCapture={onContentKeyDownCapture}
      >
        <FormCard
          container="none"
          title={title}
          titleIcon={titleIcon}
          titleSlot={resolvedTitleSlot}
          headerSlot={headerSlot}
          titleSx={titleSx}
          description={description}
          padding={padding}
          headerGap={headerGap}
          footerGap={footerGap}
          contentGap={contentGap}
          gap={gap}
          surfaceVariant={surfaceVariant}
        >
          {hasContent ? <Box sx={{ mt: s(contentOffset) }}>{children}</Box> : null}
        </FormCard>
      </DialogContent>
      {footer ? (
        <Box
          sx={{
            flex: '0 0 auto',
            px: s(resolvedPadding),
            pt: s(footerGap),
            pb: s(resolvedPadding),
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Dialog>
  );
};
